// Bekräftelsen efter betalningen (designen Web-Betalning-Klar). Stripe skickar
// tillbaka byrån hit, och sidan väntar tills Stripes händelse har kommit och
// abonnemanget är betalt (ADR 0117). Då har byrån också full åtkomst.
import { planById, planPrice, vatOn, type BillingPeriod } from '@leadledger/domain';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router';
import { data } from '../../api/client.js';
import { useBetalning } from '../../api/queries.js';
import { useAuth, useMe } from '../../auth/auth.js';
import { krMedOre } from './belopp.js';
import { FlodesHuvud } from './Flode.js';
import { stripeSida } from './stripe-sida.js';

const datum = new Intl.DateTimeFormat('sv-SE', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  timeZone: 'Europe/Stockholm',
});

/**
 * Samma dag nästa månad eller nästa år, som Stripe förnyar. Finns inte dagen i
 * den månaden blir det månadens sista dag, till exempel 31 januari till 28 februari.
 */
export function nastaBetalning(fran: Date, period: BillingPeriod): Date {
  const manader = period === 'year' ? 12 : 1;
  const ar = fran.getFullYear();
  const manad = fran.getMonth() + manader;
  const sista = new Date(ar, manad + 1, 0).getDate();
  return new Date(ar, manad, Math.min(fran.getDate(), sista));
}

function Pil() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  );
}

const NASTA = [
  {
    till: '/kom-igang',
    rubrik: 'Välj en startmall för era priser',
    text: 'Tjänster och prisförslag för svenska byråer, klara att justera.',
  },
  {
    till: '/testlage',
    rubrik: 'Prova i testläget',
    text: 'Se exakt vilken offert en förfrågan ger, innan något visas utåt.',
  },
  {
    till: '/formular',
    rubrik: 'Byt ut kontaktformuläret',
    text: 'Klistra in en rad kod. Vi hjälper till om ni vill.',
  },
] as const;

/** Stripes kundportal: kort, plan, uppsägning och fakturor. Bara ägaren. */
export function Kundportal() {
  const { api } = useAuth();
  const portal = useMutation({
    mutationFn: async () => data(await api.POST('/v1/admin/billing/portal')),
    onSuccess: ({ url }) => stripeSida.oppna(url),
  });
  return (
    <>
      <button
        type="button"
        className="btn btn-s"
        onClick={() => portal.mutate()}
        disabled={portal.isPending}
      >
        Abonnemang och kvitton
      </button>
      {portal.isError && (
        <p className="meddelande" role="alert">
          Det gick inte att öppna kundportalen. Försök igen om en stund.
        </p>
      )}
    </>
  );
}

export function BetalningKlar({ idag = new Date() }: { idag?: Date }) {
  const me = useMe();
  const queryClient = useQueryClient();
  const betalning = useBetalning({ vantaPaBetalning: true });
  const abonnemang = betalning.data?.subscription;
  const betald = abonnemang?.paid === true;
  // Efter en minut slutar sidan fråga och säger att det dröjer.
  const [lange, setLange] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setLange(true), 60_000);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    document.title = betald ? 'Planen är aktiv – LeadLedger' : 'Betalningen bekräftas – LeadLedger';
  }, [betald]);

  // Byrån har fått full åtkomst. Bannern försvinner när kontot läses om.
  useEffect(() => {
    if (betald) void queryClient.invalidateQueries({ queryKey: ['me'] });
  }, [betald, queryClient]);

  if (betalning.isError || (betalning.isSuccess && !abonnemang)) {
    return <Navigate to="/valj-plan" replace />;
  }
  if (!abonnemang || !betald) {
    return (
      <div className="kom-igang betalning">
        <FlodesHuvud aktivt={3} />
        <main id="innehall" className="kom-igang-sida">
          <div className="klar" role="status">
            <div>
              <h1>Betalningen bekräftas</h1>
              <p className="ingress">
                Vi väntar på beskedet från Stripe. Det brukar ta några sekunder.
                {lange && ' Det tar längre tid än vanligt. Ladda om sidan om en stund.'}
              </p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  const plan = planById(abonnemang.plan);
  const period: BillingPeriod = abonnemang.period;
  const netto = planPrice(plan, period);
  const moms = vatOn(netto);

  return (
    <div className="kom-igang betalning">
      <FlodesHuvud aktivt={3} />
      <main id="innehall" className="kom-igang-sida">
        <div className="klar" role="status">
          <span className="klar-bock" aria-hidden="true">
            <svg
              width="28"
              height="28"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="m5 12 5 5 9-10" />
            </svg>
          </span>
          <div>
            <h1>Klart! Planen {plan.name} är aktiv</h1>
            <p className="ingress">
              Betalningen gick igenom. Kvitto och faktura har skickats till {me.user.email}. Nu kan
              formuläret ge offerter till riktiga kunder.
            </p>
          </div>
        </div>
        <div className="betalning-kolumner klar-kolumner">
          <section className="kort sammanfattning" aria-labelledby="abonnemang-rubrik">
            <h2 id="abonnemang-rubrik">Ert abonnemang</h2>
            <div className="rad forsta">
              <span>Plan</span>
              <span>
                {plan.name}, {period === 'year' ? 'årsvis' : 'månadsvis'}
              </span>
            </div>
            <div className="rad">
              <span>Betalt i dag</span>
              <span className="mono">{krMedOre(netto + moms)}</span>
            </div>
            <div className="rad liten">
              <span>Varav moms 25 %</span>
              <span className="mono">{krMedOre(moms)}</span>
            </div>
            <div className="rad">
              <span>Nästa betalning</span>
              <span>
                {datum.format(
                  abonnemang.currentPeriodEnd
                    ? new Date(abonnemang.currentPeriodEnd)
                    : nastaBetalning(idag, period),
                )}
              </span>
            </div>
            <p className="not">
              Byt kort, byt plan, säg upp eller hämta fakturor hos Stripe. Ingen bindningstid.
            </p>
            {me.user.role === 'owner' && <Kundportal />}
          </section>
          <section className="nasta" aria-labelledby="nasta-rubrik">
            <h2 id="nasta-rubrik">Nästa steg</h2>
            <ol>
              {NASTA.map((n, i) => (
                <li key={n.till}>
                  <Link to={n.till}>
                    <span className="nummer">{i + 1}</span>
                    <span>
                      <span className="fet">{n.rubrik}</span>
                      <span className="text-3">{n.text}</span>
                    </span>
                    <Pil />
                  </Link>
                </li>
              ))}
              {/* Länken till butikerna kommer när appen finns där (steg 8.17). */}
              <li>
                <div className="utan-lank">
                  <span className="nummer">4</span>
                  <span>
                    <span className="fet">Ladda ner appen</span>
                    <span className="text-3">
                      För iPhone och Android. Godkänn offerter med ett tryck.
                    </span>
                  </span>
                </div>
              </li>
            </ol>
            <Link className="btn btn-p" to="/">
              Gå till kontot
            </Link>
          </section>
        </div>
      </main>
    </div>
  );
}
